/**
 * Controles de apariencia (se usan dentro del menú del perfil): modo, paleta, fondo, densidad y
 * esfuerzo de las tarjetas. Cada cambio se aplica al instante (no hay «Guardar»).
 * Paleta y Fondo son plegables y muestran en el resumen lo que está activo.
 */
import { useId } from 'react';
import { normalizarHex } from './color';
import { useTema } from './contexto';
import { DEGRADADOS, PALETAS } from './paletas';
import type { Densidad, Esfuerzo, Modo, PreferenciasTema } from './preferencias';
import './ControlesApariencia.css';

const MODOS: { valor: Modo; etiqueta: string }[] = [
  { valor: 'sistema', etiqueta: 'Sistema' },
  { valor: 'claro', etiqueta: 'Claro' },
  { valor: 'oscuro', etiqueta: 'Oscuro' },
];

const DENSIDADES: { valor: Densidad; etiqueta: string }[] = [
  { valor: 'comoda', etiqueta: 'Cómoda' },
  { valor: 'compacta', etiqueta: 'Compacta' },
];

const ESFUERZOS: { valor: Esfuerzo; etiqueta: string }[] = [
  { valor: 'mostrar', etiqueta: 'Mostrar' },
  { valor: 'ocultar', etiqueta: 'Ocultar' },
];

function nombrePaletaActiva(p: PreferenciasTema): string {
  return PALETAS.find((x) => x.id === p.paleta)?.nombre ?? 'Predeterminada';
}

function nombreFondoActivo(p: PreferenciasTema): string {
  const degradado = DEGRADADOS.find((d) => d.id === p.degradado);
  if (degradado) return degradado.nombre;
  return p.fondoColor ? 'Color propio' : 'Sin fondo';
}

export function ControlesApariencia() {
  const { preferencias, cambiar } = useTema();
  const id = useId();
  const colorActual = preferencias.fondoColor ?? '#ffffff';

  return (
    <div className="apariencia">
      <fieldset className="apariencia__grupo" disabled={preferencias.paleta !== null}>
        <legend>Modo</legend>
        <div className="segmentado">
          {MODOS.map((m) => (
            <button
              key={m.valor}
              type="button"
              aria-pressed={preferencias.modo === m.valor}
              onClick={() => cambiar({ modo: m.valor })}
            >
              {m.etiqueta}
            </button>
          ))}
        </div>
        {preferencias.paleta !== null && (
          <p className="apariencia__nota">
            La paleta elegida define si es clara u oscura. Elige «Predeterminada» para usar el modo.
          </p>
        )}
      </fieldset>

      <details className="apariencia__plegable">
        <summary>
          <span className="apariencia__titulo">Paleta</span>
          <span className="apariencia__actual">{nombrePaletaActiva(preferencias)}</span>
        </summary>
        <div className="apariencia__rejilla">
          <button
            type="button"
            className="muestra"
            aria-pressed={preferencias.paleta === null}
            onClick={() => cambiar({ paleta: null })}
          >
            <span className="muestra__colores muestra__colores--defecto" aria-hidden="true" />
            Predeterminada
          </button>
          {PALETAS.map((p) => (
            <button
              key={p.id}
              type="button"
              className="muestra"
              aria-pressed={preferencias.paleta === p.id}
              onClick={() => cambiar({ paleta: p.id })}
            >
              <span className="muestra__colores" aria-hidden="true">
                <span style={{ background: p.fondo }} />
                <span style={{ background: p.superficie }} />
                <span style={{ background: p.acento }} />
              </span>
              {p.nombre}
            </button>
          ))}
        </div>
      </details>

      <details className="apariencia__plegable">
        <summary>
          <span className="apariencia__titulo">Fondo</span>
          <span className="apariencia__actual">{nombreFondoActivo(preferencias)}</span>
        </summary>
        <div className="apariencia__rejilla">
          {DEGRADADOS.map((d) => (
            <button
              key={d.id}
              type="button"
              className="muestra"
              aria-pressed={preferencias.degradado === d.id}
              onClick={() => cambiar({ degradado: d.id, fondoColor: null })}
            >
              <span
                className="muestra__colores"
                aria-hidden="true"
                style={{ background: `linear-gradient(135deg, ${d.desde}, ${d.hasta})` }}
              />
              {d.nombre}
            </button>
          ))}
        </div>
        <div className="apariencia__fila">
          <label htmlFor={`${id}-color`}>Color propio</label>
          <input
            id={`${id}-color`}
            type="color"
            value={colorActual}
            onChange={(e) => {
              const hex = normalizarHex(e.target.value);
              if (hex) cambiar({ fondoColor: hex, degradado: null });
            }}
          />
          <button
            type="button"
            className="boton"
            disabled={!preferencias.fondoColor && !preferencias.degradado}
            onClick={() => cambiar({ fondoColor: null, degradado: null })}
          >
            Quitar fondo
          </button>
        </div>
      </details>

      <fieldset className="apariencia__grupo">
        <legend>Densidad de las tarjetas</legend>
        <div className="segmentado">
          {DENSIDADES.map((d) => (
            <button
              key={d.valor}
              type="button"
              aria-pressed={preferencias.densidad === d.valor}
              onClick={() => cambiar({ densidad: d.valor })}
            >
              {d.etiqueta}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="apariencia__grupo">
        <legend>Esfuerzo en las tarjetas</legend>
        <div className="segmentado">
          {ESFUERZOS.map((e) => (
            <button
              key={e.valor}
              type="button"
              aria-pressed={preferencias.esfuerzo === e.valor}
              onClick={() => cambiar({ esfuerzo: e.valor })}
            >
              {e.etiqueta}
            </button>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
